import React from "react";
import { logAudit } from "../../utils/auditLog";
import { getSupabaseClient } from "../../utils/supabase";

export function MfaChallenge({ onSuccess, onCancel }) {
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [remember, setRemember] = React.useState(true);

  async function verify() {
    const supabaseClient = getSupabaseClient();
    if (code.length !== 6) {
      setError("Please enter your 6-digit code.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data: factors } = await supabaseClient.auth.mfa.listFactors();
      const totp = factors?.totp?.[0];
      if (!totp) {
        setError("No MFA factor found.");
        setLoading(false);
        return;
      }
      const { data: challenge, error: cErr } = await supabaseClient.auth.mfa.challenge({
        factorId: totp.id,
      });
      if (cErr) {
        setError(cErr.message);
        setLoading(false);
        return;
      }
      const { error: vErr } = await supabaseClient.auth.mfa.verify({
        factorId: totp.id,
        challengeId: challenge.id,
        code: code,
      });
      if (vErr) {
        // Audit MFA challenge failures — a meaningful security signal
        // (attacker has password but not the device, or device drift).
        logAudit("mfa_challenge_failed", { factor_id: totp.id, reason: vErr.message });
        setError("Invalid code. Please try again.");
        setLoading(false);
        return;
      }
      onSuccess(remember);
    } catch (e) {
      setError(`Verification failed: ${e.message}`);
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#004D40 0%,#00695C 60%,#4DB6AC 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
      }}
    >
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h1 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>
            Zephyr Education
          </h1>
          <p style={{ color: "rgba(255,255,255,0.85)", margin: "6px 0 0", fontSize: 14 }}>
            Two-Factor Authentication
          </p>
        </div>
        <div
          style={{
            background: "white",
            borderRadius: 14,
            padding: 28,
            boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: "#00695C", marginBottom: 8 }}>
            Enter your authenticator code
          </div>
          <p style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>
            Open your authenticator app and enter the 6-digit code for Zephyr Education.
          </p>
          <input
            value={code}
            onChange={function (e) {
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
              setError("");
            }}
            onKeyDown={function (e) {
              if (e.key === "Enter") verify();
            }}
            placeholder="000000"
            maxLength={6}
            // biome-ignore lint/a11y/noAutofocus: deliberate UX — focus the OTP input when MFA prompt opens
            autoFocus
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: 24,
              textAlign: "center",
              letterSpacing: 8,
              borderRadius: 8,
              border: "1.5px solid #ccc",
              fontFamily: "monospace",
              boxSizing: "border-box",
              marginBottom: error ? 8 : 16,
            }}
          />
          {error && <div style={{ color: "#C62828", fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#666",
              marginBottom: 16,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={function (e) {
                setRemember(e.target.checked);
              }}
              style={{ width: 16, height: 16, cursor: "pointer" }}
            />
            Remember this device for 30 days
          </label>
          <button
            onClick={verify}
            disabled={loading || code.length !== 6}
            style={{
              width: "100%",
              padding: "12px",
              background: "#00695C",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 700,
              cursor: loading || code.length !== 6 ? "not-allowed" : "pointer",
              opacity: loading || code.length !== 6 ? 0.6 : 1,
              fontFamily: "inherit",
            }}
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
          <button
            onClick={onCancel}
            style={{
              width: "100%",
              marginTop: 10,
              padding: "10px",
              background: "none",
              border: "1px solid #ccc",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
              color: "#666",
              fontFamily: "inherit",
            }}
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}

export function MfaSetupRequired({ onComplete }) {
  const supabaseClient = getSupabaseClient();
  const [step, setStep] = React.useState("intro");
  const [qrCode, setQrCode] = React.useState("");
  const [secret, setSecret] = React.useState("");
  const [factorId, setFactorId] = React.useState("");
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  async function startEnroll() {
    setLoading(true);
    setError("");
    const { data, error: err } = await supabaseClient.auth.mfa.enroll({
      factorType: "totp",
      issuer: "Zephyr Education",
      friendlyName: "Zephyr Portal",
    });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setQrCode(data.totp.qr_code);
    setSecret(data.totp.secret);
    setFactorId(data.id);
    setStep("enroll");
  }

  async function verifyEnroll() {
    if (code.length !== 6) {
      setError("Please enter your 6-digit code.");
      return;
    }
    setLoading(true);
    setError("");
    const { data: challenge, error: cErr } = await supabaseClient.auth.mfa.challenge({ factorId });
    if (cErr) {
      setError(cErr.message);
      setLoading(false);
      return;
    }
    const { error: vErr } = await supabaseClient.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code,
    });
    setLoading(false);
    if (vErr) {
      setError("Invalid code \u2014 please check your app and try again.");
      return;
    }
    // Audit successful MFA enrolment \u2014 a security-sensitive change to the
    // user's account. Logged before the success-screen redirect.
    logAudit("mfa_enrolled", { factor_id: factorId, factor_type: "totp" });
    setStep("done");
    setTimeout(onComplete, 2000);
  }

  const C2 = { teal: "#00695C", white: "white", muted: "#666", border: "#ccc" };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#004D40 0%,#00695C 60%,#4DB6AC 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
      }}
    >
      <div style={{ width: "100%", maxWidth: 480 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h1 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>
            Zephyr Education
          </h1>
          <p style={{ color: "rgba(255,255,255,0.85)", margin: "6px 0 0", fontSize: 14 }}>
            Two-Factor Authentication Setup
          </p>
        </div>
        <div
          style={{
            background: "white",
            borderRadius: 14,
            padding: 28,
            boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
          }}
        >
          {step === "intro" && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C2.teal, marginBottom: 12 }}>
                MFA Required for Zephyr Staff
              </div>
              <p style={{ fontSize: 14, color: C2.muted, lineHeight: 1.6, marginBottom: 20 }}>
                As a Zephyr staff member, you are required to set up two-factor authentication
                before accessing the portal. This adds an extra layer of security to your account.
              </p>
              <p style={{ fontSize: 13, color: C2.muted, marginBottom: 20 }}>
                You will need an authenticator app such as <strong>Google Authenticator</strong>,{" "}
                <strong>Microsoft Authenticator</strong>, or <strong>Authy</strong>.
              </p>
              {error && (
                <div style={{ color: "#C62828", fontSize: 13, marginBottom: 12 }}>{error}</div>
              )}
              <button
                onClick={startEnroll}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: C2.teal,
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                {loading ? "Setting up..." : "Set Up MFA Now"}
              </button>
            </div>
          )}
          {step === "enroll" && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C2.teal, marginBottom: 12 }}>
                Scan this QR code
              </div>
              <p style={{ fontSize: 13, color: C2.muted, marginBottom: 16 }}>
                Open your authenticator app and scan the QR code below.
              </p>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img
                  src={qrCode}
                  alt="MFA QR Code"
                  style={{ width: 180, height: 180, border: "1px solid #eee", borderRadius: 8 }}
                />
              </div>
              <div
                style={{
                  background: "#f5f5f5",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 16,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 11, color: C2.muted, marginBottom: 4 }}>
                  Or enter this code manually:
                </div>
                <div
                  style={{ fontFamily: "monospace", fontSize: 13, letterSpacing: 2, color: "#333" }}
                >
                  {secret}
                </div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Enter the 6-digit code from your app:
              </div>
              <input
                value={code}
                onChange={function (e) {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setError("");
                }}
                onKeyDown={function (e) {
                  if (e.key === "Enter") verifyEnroll();
                }}
                placeholder="000000"
                maxLength={6}
                // biome-ignore lint/a11y/noAutofocus: deliberate UX — focus the OTP input during MFA enrolment
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: 24,
                  textAlign: "center",
                  letterSpacing: 8,
                  borderRadius: 8,
                  border: "1.5px solid #ccc",
                  fontFamily: "monospace",
                  boxSizing: "border-box",
                  marginBottom: error ? 8 : 16,
                }}
              />
              {error && (
                <div style={{ color: "#C62828", fontSize: 13, marginBottom: 12 }}>{error}</div>
              )}
              <button
                onClick={verifyEnroll}
                disabled={loading || code.length !== 6}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: C2.teal,
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: loading || code.length !== 6 ? "not-allowed" : "pointer",
                  opacity: loading || code.length !== 6 ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                {loading ? "Verifying..." : "Confirm & Enable MFA"}
              </button>
            </div>
          )}
          {step === "done" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>Done!</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C2.teal, marginBottom: 8 }}>
                MFA Enabled!
              </div>
              <p style={{ fontSize: 13, color: C2.muted }}>
                Your account is now protected. Taking you to the portal...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
